import React, { useState, useRef, useCallback, useEffect } from "react";
import { GoogleMap, PolygonF, useJsApiLoader } from "@react-google-maps/api";
import { Button, Typography } from "@mui/material";

import "./PolygonMap.styles.css";

const { REACT_APP_GEOCODE_API_KEY } = process.env;

const defaultCenter = { lat: -34.590283, lng: -58.430831 };

const PolygonMap = ({
  initialPath = [],
  centroid = defaultCenter,
  onPathChange,
  editable = true,
  creating = false,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: REACT_APP_GEOCODE_API_KEY,
    language: "es",
    region: "AR",
  });

  const [path, setPath] = useState(initialPath);

  const polygonRef = useRef(null);
  const listenersRef = useRef([]);
  const initialCenterRef = useRef(centroid);

  useEffect(() => {
    if (onPathChange) onPathChange(path);
  }, [path, onPathChange]);

  const onEdit = useCallback(() => {
    if (polygonRef.current) {
      const nextPath = polygonRef.current
        .getPath()
        .getArray()
        .map((latLng) => {
          return { lat: latLng.lat(), lng: latLng.lng() };
        });
      setPath(nextPath);
    }
  }, [setPath]);

  const onLoad = useCallback(
    (polygon) => {
      polygonRef.current = polygon;
      const path = polygon.getPath();
      listenersRef.current.push(
        path.addListener("set_at", onEdit),
        path.addListener("insert_at", onEdit),
        path.addListener("remove_at", onEdit)
      );
    },
    [onEdit]
  );

  const onUnmount = useCallback(() => {
    listenersRef.current.forEach((lis) => lis.remove());
    polygonRef.current = null;
  }, []);

  const onMapClick = useCallback(
    (event) => {
      if (!creating || !event.latLng) return;
      const point = { lat: event.latLng.lat(), lng: event.latLng.lng() };
      setPath((currentPath) => [...currentPath, point]);
    },
    [creating]
  );

  const removeLastPoint = useCallback(() => {
    setPath((currentPath) => currentPath.slice(0, -1));
  }, []);

  const clearPath = useCallback(() => setPath([]), []);

  return (
    <>
      {isLoaded ? (
        <>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            Polígono
          </Typography>
          {creating ? (
            <Typography variant="body2" mb={1}>
              Hacé click en el mapa para agregar puntos al polígono. Podés
              arrastrar los puntos para ajustarlos.{" "}
              <Button
                size="small"
                onClick={removeLastPoint}
                disabled={!path.length}
              >
                Deshacer punto
              </Button>
              <Button size="small" onClick={clearPath} disabled={!path.length}>
                Limpiar
              </Button>
            </Typography>
          ) : null}
          <div id="polygon-map-container">
            <GoogleMap
              mapContainerStyle={{
                height: "100%",
                width: "100%",
              }}
              center={initialCenterRef.current}
              zoom={8}
              options={{
                streetViewControl: false,
                draggableCursor: creating ? "crosshair" : undefined,
              }}
              onClick={onMapClick}
            >
              <PolygonF
                editable={editable}
                draggable={editable}
                path={path}
                onMouseUp={onEdit}
                onDragEnd={onEdit}
                onLoad={onLoad}
                onUnmount={onUnmount}
              />
            </GoogleMap>
          </div>
        </>
      ) : null}
    </>
  );
};

export default PolygonMap;
